// Money in: the CRM Finance inflows split into paid / contracted / scheduled / late / pipeline
import type { InflowItem, InflowMonth, Inflows, InflowYear, ProjectRow } from "./shared";

export const TIERS = [
  { k: "paid", label: "Paid", what: "received" },
  { k: "contracted", label: "Contracted", what: "invoiced, not yet paid" },
  { k: "scheduled", label: "Scheduled", what: "signed, not yet invoiced" },
  { k: "late", label: "Late", what: "past its date, not yet invoiced" },
  { k: "pipeline", label: "Pipeline", what: "open deals × probability" },
];
export const SEGS = ["paid", "contracted", "scheduled", "late", "pipeline"] as const;
export type Seg = (typeof SEGS)[number];
export interface Month { month: string; paid: number; contracted: number; scheduled: number; late: number; pipeline: number; items: InflowItem[] }

// The CRM puts every not-yet-invoiced amount whose date has passed into the CURRENT month with basis "late" —
// show that as its own tier, never as money expected this month
const splitMonth = (m: InflowMonth): Month => {
  const late = (m.items || []).filter((i) => i.basis === "late").reduce((s, i) => s + (i.amount || 0), 0);
  return { month: m.month, paid: m.paid || 0, contracted: m.contracted || 0, scheduled: Math.max(0, (m.scheduled || 0) - late), late, pipeline: m.pipeline || 0, items: m.items || [] };
};
export const monthTotal = (m: Month) => SEGS.reduce((s, k) => s + (m[k] || 0), 0);

export interface Money {
  months: Month[]; next12: Month[]; contracted12: number; scheduled12: number; pipeline12: number; late: number;
  yearRow: InflowYear | null; profit: number; dividendNet: number; tax: number; years: InflowYear[];
  all: Month[]; cur: number; hasPaidFeed: boolean; projects: ProjectRow[]; derived: boolean;
}

export function buildMoney(inflows: Inflows | undefined, dividendTaxPct: number | undefined): Money | null {
  if (!inflows || !Array.isArray(inflows.months)) return null;
  // The feed starts 12 months back (receipts) — split at the current month
  const all = inflows.months.map(splitMonth);
  const ym = new Date().toISOString().slice(0, 7);
  let cur = all.findIndex((m) => m.month >= ym);
  if (cur < 0) cur = 0;
  const months = all.slice(cur);
  const next12 = months.slice(0, 12);
  const hasPaidFeed = all.some((m) => m.paid > 0 || m.items.some((i) => i.tier === "paid"));
  // Per-project status: the CRM's projects[] when it sends it; otherwise the pending side only, rebuilt from the items still to come
  let projects = Array.isArray(inflows.projects) ? inflows.projects : null;
  let derived = false;
  if (!projects) {
    derived = true;
    const byKey = new Map<string, ProjectRow>();
    for (const m of months)
      for (const i of m.items) {
        if (i.tier === "pipeline" || i.tier === "paid") continue;
        const key = `${i.project}|${i.year}|${i.period || ""}`;
        const row = byKey.get(key) || { project: i.project ?? "", group: i.group, company: i.company, client: i.client, year: i.year ?? 0, period: i.period, sale: null, paid: null, outstanding: 0, status: "not-invoiced" };
        row.outstanding += i.amount || 0;
        if (i.tier === "contracted") row.status = "invoiced";
        else if (i.basis === "late" && row.status !== "invoiced") row.status = "late";
        byKey.set(key, row);
      }
    projects = [...byKey.values()];
  }
  const sum = (arr: Month[], k: Seg) => arr.reduce((s, m) => s + m[k], 0);
  const thisYear = new Date().getFullYear();
  const yearRow = (inflows.year || []).find((y) => y.year === thisYear) || null;
  const tax = Number(dividendTaxPct) || 0;
  const profit = yearRow ? yearRow.profit : 0;
  return {
    months, next12,
    contracted12: sum(next12, "contracted"), scheduled12: sum(next12, "scheduled"), pipeline12: sum(next12, "pipeline"),
    late: months[0]?.late || 0,
    yearRow, profit, dividendNet: profit * (1 - tax / 100), tax,
    years: inflows.year || [],
    all, cur, hasPaidFeed, projects, derived,
  };
}
